import { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react'
// Phase B3: access token is in memory; ensureFreshToken bootstraps it from the refresh cookie.
import { ensureFreshToken } from '../shared/api/tokenRefresh'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth.jsx'
import { identityService } from '../shared/api'
import { Button, Input, Card, LoadingSpinner } from '../components'
import { useToast } from '../components/ToastProvider.jsx'
import PhoneInput from '../shared/components/PhoneInput.jsx'
import AppHeader from '../shared/components/AppHeader.jsx'
import { LANGUAGE_NAMES, languageName, selectedLanguage } from '../shared/i18n/languages'
import CompanyLanguageSetting from '../shared/i18n/CompanyLanguageSetting.jsx'
import BrandSettings from '../modules/admin/pages/BrandSettings.jsx'
import ScheduleSettings from '../modules/admin/pages/ScheduleSettings.jsx'
import FieldTicketEmailSettings from '../modules/admin/pages/FieldTicketEmailSettings.jsx'
import ReplyToSettings from '../modules/admin/pages/ReplyToSettings.jsx'
import AiOfficeWorkerSettings from '../modules/admin/pages/AiOfficeWorkerSettings.jsx'
import SessionPolicySettings from '../modules/admin/pages/SessionPolicySettings.jsx'
import TenantBrandEditor from '../modules/admin/components/TenantBrandEditor.jsx'
import { useModuleSwitches } from '../contexts/ModuleSwitchContext.jsx'
import { isModuleReleased } from '../shared/constants/modules.js'
// "Which forms does this crew have to fill?" is an administration question, and administrators live
// here — not in the Forms module's own sub-nav, which they have no reason to visit. The SAME component
// renders in both places (one screen, one behaviour, no second copy to drift). Lazy so the Forms page
// and its project/crew/site pickers don't get pulled into the admin bundle for everyone who never
// opens this tab.
const AssignmentManager = lazy(() => import('../modules/forms/pages/AssignmentManager.jsx'))
// "Who is allowed to sign a safety record with no login, and how hard the server checks" — the
// tenant-wide rule behind the QR stickers. It used to reach this panel as a passenger on the sticker
// screen; that screen has moved to Fleet (issuing a sticker is equipment work, not paperwork
// administration) and this setting stayed behind, which is the half an administrator actually
// belongs in charge of. Same component as Forms → Signing rules, one behaviour, no second copy.
const IdentityStrictness = lazy(() => import('../modules/forms/components/IdentityStrictness.jsx'))

// ─── Tier 2: Available feature permissions ────────────────────────────────────
const ALL_PERMISSIONS = [
  // Timekeeping
  { group: 'Timekeeping',  key: 'tk:view_all',          label: 'View All Reports' },
  { group: 'Timekeeping',  key: 'tk:view_own',          label: 'View Own Reports' },
  { group: 'Timekeeping',  key: 'tk:create',            label: 'Create Reports' },
  { group: 'Timekeeping',  key: 'tk:submit',            label: 'Submit Reports' },
  { group: 'Timekeeping',  key: 'tk:review',            label: 'Review Reports (Timekeeper)' },
  { group: 'Timekeeping',  key: 'tk:approve',           label: 'Approve Reports (Superintendent)' },
  { group: 'Timekeeping',  key: 'tk:reject',            label: 'Reject Reports' },
  { group: 'Timekeeping',  key: 'tk:proxy',             label: 'Enter Time for Others (Proxy)' },
  { group: 'Timekeeping',  key: 'tk:manage_crews',      label: 'Manage Crews (create crews, set the foreman, move members)' },
  
  // Projects
  { group: 'Projects',     key: 'project:view_basic',     label: 'View Basic Project Info' },
  { group: 'Projects',     key: 'project:view_labor',     label: 'View Labor Hours (progress, no costs)' },
  { group: 'Projects',     key: 'project:sign_ticket',    label: 'Sign Field Tickets (foreman + customer sign-off)' },
  { group: 'Projects',     key: 'project:sign_release',   label: 'Sign Progress Releases (releases funds)' },
  // The link a customer signs THROUGH, for when they were not on site to sign the tablet. It mints
  // a link and nothing else: only the customer's own signature approves a ticket. Seeded to
  // foreman, superintendent, manager and office_staff; this row is what lets you hand it to one
  // more person — accounting, say — without touching a role.
  { group: 'Projects',     key: 'project:client_approval_link', label: 'Make a Customer Approval Link (send a ticket for remote sign-off)' },
  // Mail the customer the ticket they have just signed. One verb: the routes that use it pair it
  // with project:update (require_any_permission), and they refuse a ticket that is not signed.
  // It has been in the enum, the role seed and a migration since 2026-08-19 and missing from THIS
  // list ever since, which is why it could not be handed to one more person without editing a role
  // -- exactly the gap the sibling row above was added to close. The seed gives it to foreman
  // only; the migration that granted it to existing tenants also named superintendent, manager and
  // office_staff, so who holds it TODAY depends on how old the tenant is. This row is how that gets
  // settled per person, per tenant, instead of by a code change.
  { group: 'Projects',     key: 'project:email_signed_ticket', label: 'Email a Signed Ticket to the Customer' },
  { group: 'Projects',     key: 'project:view_financial', label: 'View Project Financials' },
  { group: 'Projects',     key: 'project:view_all_departments', label: 'See Projects Outside Own Department' },
  { group: 'Projects',     key: 'project:create',         label: 'Create Projects' },
  { group: 'Projects',     key: 'project:update',         label: 'Update Projects' },
  { group: 'Projects',     key: 'project:delete',         label: 'Delete Projects' },
  { group: 'Projects',     key: 'project:assign',         label: 'Assign Employees' },
  { group: 'Projects',     key: 'project:complete',       label: 'Mark Project Complete' },
  { group: 'Projects',     key: 'project:close',          label: 'Close Project' },
  
  // Finance
  { group: 'Finance',      key: 'finance:view_invoices',  label: 'View Invoices' },
  { group: 'Finance',      key: 'finance:create_invoices',label: 'Create Invoices' },
  { group: 'Finance',      key: 'finance:manage_invoices',label: 'Manage Invoices' },
  
  // HR
  { group: 'HR',           key: 'hr:view_all',            label: 'View All Employees' },
  { group: 'HR',           key: 'hr:view_own',            label: 'View Own Record' },
  { group: 'HR',           key: 'hr:manage',              label: 'Manage Employees' },
  // Deliberately NARROW, and the label has to keep it that way. It permits setting or resetting the
  // field PIN a hand with no login types at the QR-sticker door, and nothing else on the employee
  // record — no pay, no hire date, no employment status. It is additionally scoped: a holder who does
  // not also have "Manage Employees" may only do it for workers on a crew they lead. Foremen get it
  // from the role seed; this entry is what makes it grantable to one person ad hoc. The owner was
  // explicit that foremen VIEW employee records and do not EDIT them, so this must never be read, or
  // relabelled, as a small helping of hr:manage.
  { group: 'HR',           key: 'hr:manage_field_pin',    label: 'Set / Reset Field PINs (own crew — the PIN, nothing else)' },
  { group: 'HR',           key: 'hr:view_compensation',   label: 'View Compensation Data' },
  
  // CRM
  { group: 'CRM',          key: 'crm:view_clients',       label: 'View Clients' },
  { group: 'CRM',          key: 'crm:manage_clients',     label: 'Manage Clients' },
  // Capture a company at first contact and nothing else: one route, one row, from a name,
  // landing as a PROSPECT. Held with View Clients (owner, 2026-10-20).
  { group: 'CRM',          key: 'crm:create_prospect',    label: 'Add a Prospect' },
  { group: 'CRM',          key: 'crm:delete_clients',     label: 'Delete Clients' },
  { group: 'CRM',          key: 'crm:view_leads',         label: 'View Leads' },
  { group: 'CRM',          key: 'crm:manage_leads',       label: 'Manage Leads' },
  { group: 'CRM',          key: 'crm:view_opportunities', label: 'View Opportunities' },
  { group: 'CRM',          key: 'crm:manage_opportunities',label:'Manage Opportunities' },
  { group: 'CRM',          key: 'crm:view_communications',label: 'View Communications' },
  { group: 'CRM',          key: 'crm:manage_communications',label:'Manage Communications' },
  { group: 'CRM',          key: 'crm:view_engagement',    label: 'View Engagement' },
  
  // Fleet
  { group: 'Fleet',        key: 'fleet:view',             label: 'View Fleet Dashboard' },
  // The MECHANIC's grant, and — because the maintenance sheet's correction door is the same door —
  // the one a shop manager needs in order to fix the job a repair was booked to before signing it
  // off. It was checked in code by every fleet write route and offered by no catalog and no role
  // seed, which made "log a repair" and "correct a repair" both unreachable to every tenant user
  // except an admin. `documents:manage` is the cautionary tale this entry exists to avoid repeating.
  { group: 'Fleet',        key: 'fleet:edit',             label: 'Log Repairs & Edit Fleet Records' },
  { group: 'Fleet',        key: 'fleet:manage',           label: 'Manage Vehicles & Rentals' },
  { group: 'Fleet',        key: 'fleet:fuel_entry',       label: 'Submit Fuel Entries' },
  { group: 'Fleet',        key: 'fleet:inspection',       label: 'Submit Inspections' },
  // Owner, 29 Sep 2026: the mechanic (foreman) and who signs off (superintendent) see what a repair
  // sheet cost without being given project money. Seeded to both. This row is how it is moved: a
  // master admin changes a role in Admin -> Roles (role edits are master-admin only), and a company
  // admin ticks or unticks it for one person in Admin -> Users -> Access. The server's rule is Fleet's
  // routes/maintenance.py.
  { group: 'Fleet',        key: 'fleet:view_repair_costs', label: 'See repair sheet costs',
    description: "Sees and can change a maintenance sheet's parts, labour and total, its receipt amounts, " +
      'and the sign-off queue total - nothing else about money.' },

  // Inventory
  { group: 'Inventory',    key: 'inv:view',               label: 'View Inventory' },
  { group: 'Inventory',    key: 'inv:manage',             label: 'Manage Items & Locations' },
  { group: 'Inventory',    key: 'inv:scan',               label: 'Scan Items (Receive/Issue/Adjust)' },
  { group: 'Inventory',    key: 'inv:checkout',           label: 'Check Out / Check In Equipment' },
  { group: 'Inventory',    key: 'inv:reorder',            label: 'View Reorder Reports' },

  // Office Board
  { group: 'Office Board', key: 'office_board:view',      label: 'Access Office Boards' },
  { group: 'Office Board', key: 'office_board:manage',    label: 'Create & Manage Boards' },

  // Procurement - purchasing (Capex completion plan C5, docs/PURCHASING.md 4.7), served by the finance service's
  // /purchasing routes. `module: 'capex'`: purchase orders are on Project Financials jobs, so the rows are drawn
  // only while its switch is on. Matching a vendor invoice is Finance's Manage Invoices (it approves the receipt).
  { group: 'Procurement',  key: 'procurement:view',       label: 'View purchase orders and their prices', module: 'capex',
    description: 'See every purchase order with what it costs. Without it, a receiver sees quantities only.' },
  { group: 'Procurement',  key: 'procurement:create',     label: 'Make and issue purchase orders', module: 'capex',
    description: 'Make, change, issue, cancel, close and reopen purchase orders, and add a vendor to the list.' },
  { group: 'Procurement',  key: 'procurement:approve',    label: 'Approve purchase orders', module: 'capex',
    description: "Approve a purchase order at or above the company's approval threshold - never one you made." },
  { group: 'Procurement',  key: 'procurement:receive',    label: 'Receive deliveries', module: 'capex',
    description: 'Record what arrived against a purchase order (quantities, with the day), and reverse a mistake.' },
  { group: 'Procurement',  key: 'procurement:add_vendor', label: 'Add a vendor', module: 'capex',
    description: 'Put a company on the vendor list, from its name, so a bill of materials can be sent to it for a quote. Nothing else: editing and retiring vendors need Manage, and buying needs Make and issue purchase orders.' },
  { group: 'Procurement',  key: 'procurement:manage',     label: 'Manage vendors and purchasing settings', module: 'capex',
    description: 'Edit and retire vendors on the company list; set the approval threshold, the invoice tolerance and the PO number pattern.' },

  // Bid Workup
  { group: 'Bid Workup',   key: 'bid:view_bids',          label: 'View Bids' },
  { group: 'Bid Workup',   key: 'bid:create_bid',         label: 'Create Bids' },
  { group: 'Bid Workup',   key: 'bid:edit_bid',           label: 'Edit Bids' },
  { group: 'Bid Workup',   key: 'bid:delete_bid',         label: 'Delete Bids' },
  // bid:delete_draft_estimate (2026-10-22). Capex could make an estimate and never remove one, and
  // 'Delete Bids' above is held by NO seeded role - it reaches admin alone - so a throwaway estimate
  // had to be deleted by an administrator calling the endpoint by hand. THIS ROW is what lets a
  // company decide who may tidy up: it opens one route, which refuses any status but Draft, so
  // nothing sent, signed or awarded can be destroyed through it. Seeded to Estimator and Manager.
  { group: 'Bid Workup',   key: 'bid:delete_draft_estimate', label: 'Delete a draft estimate' },
  { group: 'Bid Workup',   key: 'bid:export_bid',         label: 'Export Bids' },
  { group: 'Bid Workup',   key: 'bid:manage_rates',       label: 'Manage Rates' },
  { group: 'Bid Workup',   key: 'bid:view_financials',    label: 'View Bid Financials' },
  { group: 'Bid Workup',   key: 'bid:submit_bid_request', label: 'Submit Bid Request' },
  { group: 'Bid Workup',   key: 'bid:view_bid_requests',  label: 'View Bid Requests' },
  // Signing a proposal commits the company to a scope and a price — a different question from
  // building one, which is bid:edit_bid. Manager and above by default; THIS ROW is what lets a
  // tenant move it, which is where the owner wanted the decision to live. A permission absent from
  // this catalog is one nobody can grant: fleet:edit sat in the code and in no catalog for months,
  // so its branch never fired for anyone but admin.
  { group: 'Bid Workup',   key: 'bid:sign_proposal',      label: 'Sign Proposals (bind the company)' },

  // Forms
  { group: 'Forms',        key: 'forms:design',           label: 'Design Forms (Form Builder)' },
  { group: 'Forms',        key: 'forms:publish',          label: 'Publish Form Versions' },
  { group: 'Forms',        key: 'forms:assign',           label: 'Assign Forms to Projects/Crews' },
  { group: 'Forms',        key: 'forms:submit',           label: 'Fill & Submit Own Forms' },
  { group: 'Forms',        key: 'forms:submit_for',       label: 'Submit Forms for Others (Proxy)' },
  { group: 'Forms',        key: 'forms:review',           label: 'Review Submitted Forms' },
  { group: 'Forms',        key: 'forms:approve',          label: 'Approve Submitted Forms' },
  { group: 'Forms',        key: 'forms:reopen',           label: 'Reopen Approved Forms' },
  { group: 'Forms',        key: 'forms:view_all',         label: 'View All Form Submissions' },
  { group: 'Forms',        key: 'forms:view_defects',     label: 'View Reported Defects & Corrective Actions' },
  { group: 'Forms',        key: 'forms:export',           label: 'Export Forms (PDF / CSV)' },
  { group: 'Forms',        key: 'forms:manage_bindings',  label: 'Manage Form Bindings' },

  // Capital Projects. Two permissions and no more: whether you can SEE the capital register,
  // and whether you can PUT A PROJECT IN IT. A permission absent from this list is ungrantable
  // through the product however correctly the backend enforces it.
  //
  // `module` marks a row that exists ONLY for a held module (update 2): it is not drawn while that
  // module's server switch is off. Capex and Invoicing have no such rows — they gate on
  // project:view_financial and finance:manage_invoices, which Projects and Finance use today, so
  // hiding those would hide live permissions of two released modules.
  { group: 'Capital',      key: 'capital:read',           label: 'View Capital Projects', module: 'capital' },
  { group: 'Capital',      key: 'capital:manage',         label: 'Make a Project a Capital Project', module: 'capital' },

  // Other Existing
  { group: 'Documents',    key: 'documents.view',         label: 'View Documents' },
  { group: 'Documents',    key: 'documents.upload',       label: 'Upload Documents' },
  { group: 'Documents',    key: 'documents.admin',        label: 'Manage Document Workflows' },
  // Dot form, matching `documents.view` above rather than the colon everything else uses — the two
  // modules are siblings and are read side by side. It has to be spelled identically here, in
  // shared/auth/permissions.py and in the migration, or the checkbox grants a string nothing checks.
  { group: 'PDF Reader',   key: 'pdfreader.view',         label: 'View PDF Reader (drawings & take-offs)' },
  { group: 'Reports',      key: 'reports.view',           label: 'View Reports' },
  { group: 'Admin',        key: 'admin.users',            label: 'Manage Users (Admin Panel)' },
]

// ─── Tier 3: Modules that support record-level access ─────────────────────────
const RECORD_ACCESS_MODULES = [
  { id: 'projects',  label: 'Projects' },
  { id: 'crm',       label: 'CRM / Clients' },
  { id: 'documents', label: 'Documents' },
]

// Document types for the documents Tier 3 restriction picker
const DOCUMENT_TYPES = [
  { value: 'PROPOSAL',               label: 'Proposal' },
  { value: 'CHANGE_ORDER',           label: 'Change Order' },
  { value: 'FIELD_WORK_AUTHORIZATION', label: 'Field Work Authorization' },
  { value: 'CLOSEOUT_PACKAGE',       label: 'Closeout Package' },
  { value: 'SUBCONTRACT',            label: 'Subcontract' },
  { value: 'PURCHASE_ORDER',         label: 'Purchase Order' },
  { value: 'INSURANCE_CERTIFICATE',  label: 'Insurance Certificate' },
  { value: 'PERMIT',                 label: 'Permit' },
  { value: 'INSPECTION_REPORT',      label: 'Inspection Report' },
  { value: 'SAFETY_DOCUMENT',        label: 'Safety Document' },
  { value: 'MEETING_MINUTES',        label: 'Meeting Minutes' },
  { value: 'SUBMITTAL',              label: 'Submittal' },
  { value: 'RFI',                    label: 'RFI' },
  { value: 'DAILY_REPORT',           label: 'Daily Report' },
  { value: 'OTHER',                  label: 'Other' },
]

const ALL_MODULES = [
  { id: 'identity',    name: 'My Profile',               icon: '👤' },
  { id: 'bid_request', name: 'Bid Request',               icon: '📋' },
  { id: 'timekeeping', name: 'Timekeeping',               icon: '⏰' },
  { id: 'projects',    name: 'Projects',                  icon: '📊' },
  { id: 'crm',         name: 'CRM',                       icon: '🤝' },
  { id: 'hr',          name: 'HR Management',             icon: '👥' },
  { id: 'bidworkup',   name: 'Bid Workup',                icon: '🏗️' },
  { id: 'compliance',  name: 'Compliance & Inspections',  icon: '✓',  comingSoon: true },
  { id: 'documents',   name: 'Document Control',          icon: '📄' },
  { id: 'finance',     name: 'Finance & Accounting',      icon: '💰' },
  { id: 'fleet',       name: 'Fleet Management',          icon: '🚛' },
  { id: 'inventory',   name: 'Inventory',                 icon: '📦' },
  { id: 'officeboard', name: 'Office Board',              icon: '📋' },
  { id: 'pdfreader',   name: 'PDF Reader',                icon: '📐' },
  { id: 'procurement', name: 'Procurement',               icon: '🛒' },
  // Forms was absent from this catalog, so the role editor rendered no Forms toggle — there was no
  // way to grant the module through the UI at all, on any tenant.
  { id: 'forms',       name: 'Forms',                     icon: '📝' },
  { id: 'capital',     name: 'Capital Projects',          icon: '🏛️' },
  // Without a row here the Roles editor renders no toggle, so the module cannot be granted
  // through the UI on any tenant — which is exactly how Forms stayed invisible for months.
  { id: 'capex',       name: 'Project Financials',        icon: '📉' },
  // Invoicing — capex's twin (same permission, same roles). Present here for the same reason:
  // without a row the Roles editor renders no toggle and the module cannot be granted through
  // the UI on any tenant.
  { id: 'invoicing',   name: 'Invoicing',                 icon: '🧾' },
  // The AI Office Worker (design change H2). A row here so the Roles editor can grant its tab; the
  // tab still shows only where both module-switch keys are on (shared/constants/modules.js).
  { id: 'office_worker', name: 'Office Worker',           icon: '🤖' },
]

// Role-based default module access — synced with backend session.py _SYSTEM_ROLES (lean set).
// This is only a pre-load FALLBACK for module display; the authoritative role list + module
// grants come from the roles API (identityService.getRoles). Keep in sync when _SYSTEM_ROLES
// changes. 'master_admin' is retained as a platform super-role for existing platform users.
// 'forms' is present on every role but 'client' — it is a standard always-on module, matching
// backend session.py _SYSTEM_ROLES. See that file for why the earlier "un-gating" had no effect.
const ROLE_DEFAULT_MODULES = {
  'admin':          ['identity','projects','timekeeping','documents','crm','hr','finance','compliance','bid_request','bidworkup','fleet','inventory','officeboard','pdfreader','procurement','forms','capital'],
  'manager':        ['identity','projects','timekeeping','documents','crm','finance','compliance','bid_request','bidworkup','fleet','inventory','officeboard','forms','capital'],
  'superintendent': ['identity','projects','timekeeping','documents','compliance','fleet','inventory','officeboard','forms'],
  'foreman':        ['identity','projects','timekeeping','documents','bid_request','fleet','inventory','officeboard','forms'],
  'estimator':      ['identity','projects','documents','crm','bid_request','bidworkup','officeboard','forms'],
  'accounting':     ['identity','finance','hr','timekeeping','documents','inventory','officeboard','forms','capital'],
  'office_staff':   ['identity','projects','timekeeping','documents','crm','officeboard','forms'],
  'employee':       ['identity','timekeeping','officeboard','forms'],
  'client':         ['identity'],
  'master_admin':   ['identity','projects','timekeeping','documents','crm','hr','finance','compliance','bid_request','bidworkup','fleet','inventory','officeboard','pdfreader','procurement','forms','capital'],
}

// Fallback helper when API roles haven't loaded yet
const getRoleDefaultModulesFallback = (role) => {
  const normalizedRole = role?.toLowerCase() || 'employee'
  return ROLE_DEFAULT_MODULES[normalizedRole] || []
}

// Color -> tailwind badge classes for role cards
const ROLE_COLOR_CLASSES = {
  red:     'bg-red-100 text-red-800 border border-red-200',
  purple:  'bg-purple-100 text-purple-800 border border-purple-200',
  blue:    'bg-blue-100 text-blue-800 border border-blue-200',
  green:   'bg-green-100 text-green-800 border border-green-200',
  yellow:  'bg-yellow-100 text-yellow-800 border border-yellow-200',
  gray:    'bg-gray-100 text-gray-800 border border-gray-200',
  orange:  'bg-orange-100 text-orange-800 border border-orange-200',
  teal:    'bg-teal-100 text-teal-800 border border-teal-200',
  indigo:  'bg-indigo-100 text-indigo-800 border border-indigo-200',
  pink:    'bg-pink-100 text-pink-800 border border-pink-200',
  fuchsia: 'bg-fuchsia-100 text-fuchsia-800 border border-fuchsia-200',
}
const ROLE_COLORS = Object.keys(ROLE_COLOR_CLASSES)

const Admin = () => {
  const { isAdmin, loading: authLoading, validateAuthentication, user, updateUserRole } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()

  // HELD MODULES ARE NOT OFFERED HERE EITHER (update 2, owner decision). While a module's server
  // switch is off (contexts/ModuleSwitchContext.jsx — fails closed while loading and on any error),
  // the Roles editor and the per-user editors draw no toggle for Capital Projects, Project Financials
  // or Invoicing, and no permission row that exists only for one of them. A toggle for a module
  // nobody can open is a grant that looks like it did something.
  //
  // HIDE ONLY — NOTHING STORED CHANGES. These lists decide what is DRAWN and nothing else: every save
  // below still sends the state it loaded (role.default_modules, role.default_permissions, a user's
  // accessible_modules, their effective permissions), and the per-user permission save still sends
  // the FULL catalogue as `known`. That last one is load-bearing: the server re-derives a user's
  // denies only within `known`, so dropping a hidden permission from it would silently delete a deny
  // row an admin set (routers/user.py set_user_permissions).
  const { switches: moduleSwitches } = useModuleSwitches()
  const visibleModules = ALL_MODULES.filter(m => isModuleReleased(m.id, moduleSwitches))
  const visiblePermissions = ALL_PERMISSIONS.filter(p => !p.module || isModuleReleased(p.module, moduleSwitches))
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [showRegisterForm, setShowRegisterForm] = useState(false)
  const [registrationSuccess, setRegistrationSuccess] = useState(false)
  const [lastRegisteredUser, setLastRegisteredUser] = useState(null)
  const [editingModules, setEditingModules] = useState(null)
  const [selectedModules, setSelectedModules] = useState([])
  const [savingModules, setSavingModules] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'employee',
    phone: '',
    // '' means "not set" — the new person's device and the company default decide. NOT 'en': a
    // default of English here would make every account created from this form look like it had
    // CHOSEN English, which outranks both. Same reason the column is nullable.
    language: ''
  })
  const [formErrors, setFormErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [tokenValidated, setTokenValidated] = useState(false)
  const [roleChanges, setRoleChanges] = useState({})
  const [confirmingRoleChange, setConfirmingRoleChange] = useState(null)

  // ── Admin Password Reset modal state ──────────────────────────────────────
  const [resetPasswordUser, setResetPasswordUser] = useState(null)
  const [resetPasswordValue, setResetPasswordValue] = useState('')
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('')
  const [resetPasswordError, setResetPasswordError] = useState('')
  const [resettingPassword, setResettingPassword] = useState(false)

  // ── Permissions & Record Access modal state ──────────────────────────────
  const [accessModalUser, setAccessModalUser] = useState(null)      // user being edited
  const [accessModalTab, setAccessModalTab] = useState('permissions') // 'permissions' | 'record'
  const [editingPermissions, setEditingPermissions] = useState([])   // Tier 2 flags
  const [editingAccessModes, setEditingAccessModes] = useState({})   // Tier 3 modes
  const [editingRecordGrants, setEditingRecordGrants] = useState({}) // Tier 3 grants by module
  const [recordGrantProjects, setRecordGrantProjects] = useState([])   // available projects list
  const [recordGrantClients, setRecordGrantClients] = useState([])   // available CRM clients
  const [recordGrantDocuments, setRecordGrantDocuments] = useState([])   // available documents list
  const [accessModalLoading, setAccessModalLoading] = useState(false)
  const [savingAccess, setSavingAccess] = useState(false)

  // ── Dynamic Role management state ─────────────────────────────────────────
  const [roles, setRoles] = useState([])
  const [rolesLoading, setRolesLoading] = useState(false)
  const [roleModalOpen, setRoleModalOpen] = useState(false)
  const [editingRole, setEditingRole] = useState(null)  // null = creating new
  const [roleForm, setRoleForm] = useState({
    name: '', display_name: '', description: '', color: 'gray',
    default_modules: [], default_permissions: []
  })
  const [savingRole, setSavingRole] = useState(false)
  const [deletingRoleId, setDeletingRoleId] = useState(null)

  // ── Tab navigation ─────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('users')

  // Same test the Forms module's own sub-nav uses for the Assignments tab, so the two surfaces can't
  // disagree about who may see it. The backend gates the writes regardless (forms:assign).
  const canAssignForms = ['admin', 'master_admin'].includes(user?.role)
    || (user?.permissions || []).includes('forms:assign')

  // API-aware role default modules lookup (falls back to hardcoded map)
  const getRoleDefaultModules = useCallback((role) => {
    const normalizedRole = role?.toLowerCase() || 'employee'
    if (roles.length > 0) {
      const found = roles.find(r => r.name === normalizedRole)
      if (found) return found.default_modules || []
    }
    return getRoleDefaultModulesFallback(normalizedRole)
  }, [roles])

  // The role's default modules as the modules modal names them: only the ones drawn. Empty ('') when every
  // default is a held module, so the sentence says "No modules" instead of ending on "has default access
  // to: " with nothing after it.
  const visibleRoleDefaultNames = (role) => getRoleDefaultModules(role)
    .map(id => visibleModules.find(m => m.id === id)?.name)
    .filter(Boolean)
    .join(', ')

  // First, validate token before loading admin content
  useEffect(() => {
    const validateAdminAccess = async () => {
      try {
        console.log('Admin access validation started', { isAdmin: isAdmin(), tokenValidated });
        
        if (!isAdmin()) {
          console.log('Not an admin, redirecting to home');
          navigate('/');
          return;
        }
        
        // Only validate if not already validated
        if (!tokenValidated) {
          console.log('Validating admin token...');
          const isValid = await validateAuthentication();
          console.log('Token validation result:', isValid);
          setTokenValidated(isValid);
          
          if (!isValid) {
            console.log('Token validation failed, redirecting to login');
            navigate('/login');
          }
        }
      } catch (err) {
        console.error('Token validation error:', err);
        // Let the API interceptor handle the redirect to login
      }
    };
    
    validateAdminAccess();
  }, [isAdmin, validateAuthentication, tokenValidated, navigate])

  // Load users function - must be declared before the useEffect that uses it
  const loadUsers = useCallback(async () => {
    try {
      setLoading(true)
      const response = await identityService.getUsers()
      
      // Filter out master admins and other admins if current user is a regular admin
      let filteredUsers = response
      if (user?.role === 'admin') {
        filteredUsers = response.filter(userItem => 
          userItem.role !== 'master_admin' && 
          (userItem.role !== 'admin' || userItem.id === user.id)
        )
      }
      
      setUsers(filteredUsers)
    } catch (err) {
      console.error('Error loading users:', err)
    } finally {
      setLoading(false)
    }
  }, [user?.role, user?.id])

  const loadRoles = useCallback(async () => {
    try {
      setRolesLoading(true)
      const data = await identityService.getRoles()
      setRoles(data)
    } catch (err) {
      console.error('Error loading roles:', err)
    } finally {
      setRolesLoading(false)
    }
  }, [])

  // Load users and roles after successful validation
  useEffect(() => {
    console.log('Load users effect triggered', { tokenValidated, isAdmin: isAdmin() });
    if (tokenValidated && isAdmin()) {
      console.log('Loading users after validation');
      loadUsers();
      loadRoles();
    }
  }, [tokenValidated, isAdmin, loadUsers, loadRoles])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear field error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    if (!formData.password) {
      newErrors.password = 'Password is required'
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long'
    }

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required'
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required'
    }

    if (formData.phone && !/^\+?[\d\s\-()]+$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number'
    }

    setFormErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleRegisterSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) return

    try {
      setSubmitting(true)
      
      // Transform form data to match backend schema
      const registrationData = {
        name: `${formData.first_name} ${formData.last_name}`.trim(),
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone || null,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        // Set at ONBOARDING, by the person doing the onboarding. This is the half of the answer that
        // does not require the new hire to find a settings screen written in a language they cannot
        // read. Omitted when blank, so "not set" stays distinguishable from "chose English".
        ...(formData.language ? { language: formData.language } : {})
      }
      
      const newUser = await identityService.register(registrationData)
      
      // Optimistically add the new user to the list immediately
      setUsers(prevUsers => [...prevUsers, newUser])
      
      // Store the registered user info for the success banner
      setLastRegisteredUser({
        name: `${registrationData.first_name} ${registrationData.last_name}`,
        email: registrationData.email,
        role: registrationData.role
      })
      
      // Show success banner
      setRegistrationSuccess(true)
      
      // Reset form and close
      setFormData({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        role: 'employee',
        phone: '',
        language: ''
      })
      setFormErrors({})
      setShowRegisterForm(false)
      
      // Reload in background to ensure consistency
      loadUsers()
      
      // Auto-hide success banner after 10 seconds
      setTimeout(() => {
        setRegistrationSuccess(false)
      }, 10000)
      
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'An unexpected error occurred';
      toast.show({
        title: '✗ User Registration Failed',
        message: `Could not create account for ${formData.email}. ${errorMessage}`,
        type: 'error'
      });
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"?`)) {
      return
    }

    try {
      // Optimistically remove user from UI immediately
      setUsers(prevUsers => prevUsers.filter(u => u.id !== userId))
      
      await identityService.deleteUser(userId)
      
      toast.show({
        title: '✓ User Deleted Successfully',
        message: `${userName} has been permanently removed from the system`,
        type: 'success'
      })
      
      // Reload to ensure consistency with server
      await loadUsers()
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'An unexpected error occurred'
      toast.show({
        title: '✗ User Deletion Failed',
        message: `Could not delete ${userName}. ${errorMessage}`,
        type: 'error'
      })
      // Reload on error to restore the list
      await loadUsers()
    }
  }
  
  /**
   * Set (or clear) which language another user reads the application in.
   *
   * OPTIMISTIC, and deliberately so: this is a display preference, the failure mode is that the list
   * shows the wrong value until the next load, and blocking an admin's whole users table on a network
   * round trip for a language dropdown would be worse than that. A failure is surfaced and the row is
   * put back.
   */
  const handleUserLanguageChange = async (userId, tag) => {
    const previous = users.find(u => u.id === userId)?.language ?? null
    const next = tag || null
    setUsers(prev => prev.map(u => (u.id === userId ? { ...u, language: next } : u)))
    try {
      await identityService.setUserLanguage(userId, next)
    } catch (error) {
      setUsers(prev => prev.map(u => (u.id === userId ? { ...u, language: previous } : u)))
      toast.showError(
        error.response?.data?.detail || 'That language could not be saved. Try again.'
      )
    }
  }

  const handleToggleActive = async (userItem) => {
    const userName = userItem.first_name && userItem.last_name
      ? `${userItem.first_name} ${userItem.last_name}`
      : userItem.name || userItem.email;
    const action = userItem.is_active ? 'deactivate' : 'reactivate';
    if (!window.confirm(`Are you sure you want to ${action} user "${userName}"?`)) {
      return;
    }
    try {
      await identityService.toggleUserActive(userItem.id);
      toast.show({
        title: userItem.is_active ? '✓ User Deactivated' : '✓ User Reactivated',
        message: `${userName} has been ${action}d`,
        type: 'success'
      });
      await loadUsers();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'An unexpected error occurred';
      toast.show({
        title: `✗ Could not ${action} user`,
        message: errorMessage,
        type: 'error'
      });
    }
  }
  
  // Handle initial role change selection
  const handleRoleChange = (userId, newRole) => {
    setRoleChanges(prev => ({
      ...prev,
      [userId]: newRole
    }))
    
    // Set this user as the one with a pending confirmation
    setConfirmingRoleChange(userId)
  }
  
  // Handle confirming the role change
  const handleRoleChangeConfirm = async (userId, userName, currentRole, newRole) => {
    if (!newRole || newRole === currentRole) {
      setConfirmingRoleChange(null)
      return
    }
    
    try {
      const result = await updateUserRole(userId, newRole)
      
      if (result.success) {
        toast.show({
          title: '✓ User Role Updated Successfully',
          message: `${userName}'s role has been changed from ${currentRole} to ${newRole}`,
          type: 'success'
        })
      } else {
        throw new Error(result.error)
      }
      
      // Reset confirming state
      setConfirmingRoleChange(null)
      setRoleChanges(prev => ({
        ...prev,
        [userId]: null
      }))
      
      // Reload users to get updated data
      await loadUsers()
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'An unexpected error occurred'
      
      toast.show({
        title: '✗ Role Update Failed',
        message: `Could not update ${userName}'s role. ${errorMessage}`,
        type: 'error'
      })
      
      // Reset confirming state
      setConfirmingRoleChange(null)
      setRoleChanges(prev => ({
        ...prev,
        [userId]: null
      }))
    }
  }

  // ── Admin Password Reset handlers ──────────────────────────────────────────
  const handleOpenResetPassword = (userItem) => {
    setResetPasswordUser(userItem)
    setResetPasswordValue('')
    setResetPasswordConfirm('')
    setResetPasswordError('')
  }

  const handleCloseResetPassword = () => {
    setResetPasswordUser(null)
    setResetPasswordValue('')
    setResetPasswordConfirm('')
    setResetPasswordError('')
  }

  const handleResetPasswordSubmit = async () => {
    // Validate
    if (!resetPasswordValue) {
      setResetPasswordError('Password is required')
      return
    }
    if (resetPasswordValue.length < 8) {
      setResetPasswordError('Password must be at least 8 characters')
      return
    }
    if (resetPasswordValue !== resetPasswordConfirm) {
      setResetPasswordError('Passwords do not match')
      return
    }

    setResettingPassword(true)
    setResetPasswordError('')
    try {
      await identityService.adminSetPassword(resetPasswordUser.id, resetPasswordValue)
      toast.show({
        title: '✓ Password Updated',
        message: `Password for ${resetPasswordUser.name} has been changed successfully`,
        type: 'success'
      })
      handleCloseResetPassword()
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to set password'
      setResetPasswordError(errorMessage)
    } finally {
      setResettingPassword(false)
    }
  }

  // Module Access Management Functions
  const handleEditModules = (userItem) => {
    setEditingModules(userItem)
    setSelectedModules(userItem.accessible_modules || [])
  }

  const handleModuleToggle = (moduleId) => {
    setSelectedModules(prev => {
      // If no custom overrides yet, seed from role defaults before toggling
      const base = prev.length === 0 ? getRoleDefaultModules(editingModules?.role) : prev
      if (base.includes(moduleId)) {
        return base.filter(id => id !== moduleId)
      } else {
        return [...base, moduleId]
      }
    })
  }

  const handleResetToDefault = async () => {
    if (!editingModules) return

    setSavingModules(true)
    try {
      // Send empty array to clear custom modules and use role defaults
      const response = await identityService.updateUserModules(editingModules.id, [])
      
      // Update the user in the list
      setUsers(prev => prev.map(u => 
        u.id === response.id ? response : u
      ))

      // Close the dialog first
      setEditingModules(null)
      setSelectedModules([])
      setSavingModules(false)

      // Then show success message
      toast.show({
        title: '✓ Reset to Role Defaults',
        message: `${editingModules.name} will now use their role's default module access`,
        type: 'success'
      })
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to reset module access'
      
      // Keep dialog open on error, just show the error message
      setSavingModules(false)
      
      toast.show({
        title: '✗ Reset Failed',
        message: `Could not reset access for ${editingModules.name}. ${errorMessage}`,
        type: 'error'
      })
    }
  }

  const handleSaveModules = async () => {
    if (!editingModules) return

    setSavingModules(true)
    try {
      const response = await identityService.updateUserModules(editingModules.id, selectedModules)
      
      // Update the user in the list
      setUsers(prev => prev.map(u => 
        u.id === response.id ? response : u
      ))

      // Determine the message based on what was changed
      let message = ''
      if (selectedModules.length === 0) {
        message = `${editingModules.name} will now use their role's default module access`
      } else if (selectedModules.length === 1) {
        message = `${editingModules.name} now has access to ${selectedModules.length} custom module`
      } else {
        message = `${editingModules.name} now has access to ${selectedModules.length} custom modules`
      }

      // Close the dialog first
      setEditingModules(null)
      setSelectedModules([])
      setSavingModules(false)

      // Then show success message
      toast.show({
        title: '✓ Module Access Updated Successfully',
        message: message,
        type: 'success'
      })
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to update module access'
      
      // Keep dialog open on error, just show the error message
      setSavingModules(false)
      
      toast.show({
        title: '✗ Module Access Update Failed',
        message: `Could not update access for ${editingModules.name}. ${errorMessage}`,
        type: 'error'
      })
    }
  }

  const handleCancelEditModules = () => {
    setEditingModules(null)
    setSelectedModules([])
  }

  // ── Permissions & Record Access modal handlers ────────────────────────────

  const handleOpenAccessModal = async (userItem) => {
    setAccessModalUser(userItem)
    setAccessModalTab('permissions')
    setAccessModalLoading(true)
    try {
      const [perms, modes] = await Promise.all([
        identityService.getUserPermissions(userItem.id),
        identityService.getUserAccessModes(userItem.id),
      ])
      setEditingPermissions(perms)
      setEditingAccessModes(modes)
      // Load record grants for any restricted modules
      const grants = {}
      for (const [mod, mode] of Object.entries(modes)) {
        if (mode === 'restricted') {
          grants[mod] = await identityService.getUserRecordGrants(userItem.id, mod)
        }
      }
      setEditingRecordGrants(grants)
      // Load project list for record picker
      try {
        const token = await ensureFreshToken()
        const resp = await fetch('/api/projects/projects/summary?is_active=true', {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        })
        if (resp.ok) setRecordGrantProjects(await resp.json())
      } catch (_) { /* projects service may be down */ }
      // Load CRM client list for record picker
      try {
        const token = await ensureFreshToken()
        const resp = await fetch('/api/crm/clients/search?is_active=true&page_size=200&sort_by=company_name', {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        })
        if (resp.ok) {
          const data = await resp.json()
          setRecordGrantClients(data.items || [])
        }
      } catch (_) { /* CRM service may be down */ }
      // Load documents list for record picker
      try {
        const token = await ensureFreshToken()
        const resp = await fetch('/api/documents/documents/search?page_size=200', {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        })
        if (resp.ok) {
          const data = await resp.json()
          setRecordGrantDocuments(data.results || [])
        }
      } catch (_) { /* Documents service may be down */ }
    } catch (err) {
      toast.show({ title: '✗ Error', message: 'Could not load permission data', type: 'error' })
    } finally {
      setAccessModalLoading(false)
    }
  }

  const handleCloseAccessModal = () => {
    setAccessModalUser(null)
    setEditingPermissions([])
    setEditingAccessModes({})
    setEditingRecordGrants({})
  }

  const handlePermissionToggle = (key) => {
    setEditingPermissions(prev =>
      prev.includes(key) ? prev.filter(p => p !== key) : [...prev, key]
    )
  }

  const handleAccessModeToggle = async (mod, newMode) => {
    // Optimistic update
    setEditingAccessModes(prev => ({ ...prev, [mod]: newMode }))
    if (newMode === 'open') {
      setEditingRecordGrants(prev => { const n = { ...prev }; delete n[mod]; return n })
    } else if (!(editingRecordGrants[mod])) {
      // Load existing grants if switching to restricted
      try {
        const grants = await identityService.getUserRecordGrants(accessModalUser.id, mod)
        setEditingRecordGrants(prev => ({ ...prev, [mod]: grants }))
      } catch (_) {
        setEditingRecordGrants(prev => ({ ...prev, [mod]: [] }))
      }
    }
  }

  const handleRecordGrantToggle = (mod, recordId) => {
    setEditingRecordGrants(prev => {
      const current = prev[mod] || []
      return {
        ...prev,
        [mod]: current.includes(recordId)
          ? current.filter(id => id !== recordId)
          : [...current, recordId],
      }
    })
  }

  const handleSaveAccess = async () => {
    if (!accessModalUser) return
    setSavingAccess(true)
    try {
      // Save Tier 2 permissions. Send the full catalog so the server only removes permissions this
      // panel actually showed (never silently drops a role-default the UI didn't render).
      // The FULL catalog even while a held module's rows are hidden: `editingPermissions` still holds
      // what was loaded for them, so their grants and denies are re-sent exactly as they were.
      await identityService.setUserPermissions(
        accessModalUser.id, editingPermissions, ALL_PERMISSIONS.map(p => p.key)
      )

      // Save Tier 3 access modes and record grants
      for (const mod of RECORD_ACCESS_MODULES.map(m => m.id)) {
        const mode = editingAccessModes[mod] || 'open'
        await identityService.setUserAccessMode(accessModalUser.id, mod, mode)
        if (mode === 'restricted') {
          await identityService.setUserRecordGrants(
            accessModalUser.id, mod, editingRecordGrants[mod] || []
          )
        }
      }

      toast.show({
        title: '✓ Access Updated',
        message: `Permissions saved for ${accessModalUser.name}`,
        type: 'success',
      })
      handleCloseAccessModal()
    } catch (err) {
      toast.show({
        title: '✗ Save Failed',
        message: err.response?.data?.detail || 'Could not save access settings',
        type: 'error',
      })
    } finally {
      setSavingAccess(false)
    }
  }

  // ── Role handlers ──────────────────────────────────────────────────────────
  const handleSaveRole = async () => {
    if (!roleForm.display_name.trim()) {
      toast.showError('Display name is required')
      return
    }
    if (!editingRole && !roleForm.name.trim()) {
      toast.showError('Role name (slug) is required')
      return
    }
    setSavingRole(true)
    try {
      if (editingRole) {
        await identityService.updateRole(editingRole.id, {
          display_name: roleForm.display_name,
          description: roleForm.description,
          color: roleForm.color,
          default_modules: roleForm.default_modules,
          default_permissions: roleForm.default_permissions,
        })
        toast.showSuccess(`Role "${roleForm.display_name}" updated`)
      } else {
        await identityService.createRole({
          name: roleForm.name.trim().toLowerCase().replace(/\s+/g, '_'),
          display_name: roleForm.display_name,
          description: roleForm.description,
          color: roleForm.color,
          default_modules: roleForm.default_modules,
          default_permissions: roleForm.default_permissions,
        })
        toast.showSuccess(`Role "${roleForm.display_name}" created`)
      }
      setRoleModalOpen(false)
      loadRoles()
    } catch (err) {
      toast.showError(err?.response?.data?.detail || 'Failed to save role')
    } finally {
      setSavingRole(false)
    }
  }

  const handleRoleFormModuleToggle = (moduleId) => {
    setRoleForm(prev => ({
      ...prev,
      default_modules: prev.default_modules.includes(moduleId)
        ? prev.default_modules.filter(m => m !== moduleId)
        : [...prev.default_modules, moduleId]
    }))
  }

  const handleRoleFormPermToggle = (permKey) => {
    setRoleForm(prev => ({
      ...prev,
      default_permissions: prev.default_permissions.includes(permKey)
        ? prev.default_permissions.filter(p => p !== permKey)
        : [...prev.default_permissions, permKey]
    }))
  }

  // Show loading spinner while checking admin status
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" text="Loading..." />
      </div>
    )
  }

  // Show access denied if not admin
  if (!isAdmin()) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md p-8 bg-white rounded-lg shadow-lg text-center">
          <div className="mb-6">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.498 0L4.316 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Access Denied</h1>
            <p className="text-gray-600 mb-2">You don&apos;t have permission to access the admin panel.</p>
            <p className="text-sm text-gray-500">This area is restricted to administrators only.</p>
          </div>
          
          <div className="space-y-3">
            <Button
              variant="primary"
              onClick={() => navigate('/profile')}
              className="w-full"
            >
              Go to Profile
            </Button>
            
            <Button
              variant="secondary"
              onClick={() => navigate(-1)}
              className="w-full"
            >
              Go Back
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50" style={{ display: 'flex', flexDirection: 'column' }}>
      <AppHeader 
        title="Administration Panel"
        showAdminButton={false}
        user={user}
      />
      
      {/* `w-full` IS WHY THIS PAGE WAS 1085px WIDE ON A 390px PHONE, AND IT IS NOT THE TAB BAR.
          Measured, because the obvious answers were both wrong. The wrapper above is
          `display: flex; flex-direction: column`, so this div is a flex item. A flex item with
          `width: auto` normally STRETCHES to the container's 390px — except that `mx-auto` sets
          cross-axis auto margins, and an auto margin in the cross axis switches stretch off. With
          no stretch the item falls back to its fit-content width, which is its min-content width,
          which the user table pins at 1085px. It then dragged every child over the edge with it:
          `⚙️ Settings` 0 of 86px on screen, `Register New User` 0 of 173px, and six table headers
          gone entirely.
          `overflow-x-auto` on the table wrapper does not help — that was already there. Nor does
          `min-w-0`: in a COLUMN flex container `min-width: auto` already resolves to 0, so there
          was nothing to relax. An explicit width is what makes the item take the 390px it was
          offered. `max-w-7xl` still caps it at 1280 and `mx-auto` still centres it on a monitor. */}
      <div className="max-w-7xl w-full mx-auto py-8 px-4 sm:px-6 lg:px-8 min-w-0" style={{ flex: 1 }}>

        {/* Success Banner */}
        {registrationSuccess && lastRegisteredUser && (
          <div className="mb-6 bg-green-50 border-l-4 border-green-400 p-4 rounded-md">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-lg font-medium text-green-800">
                  User Registered Successfully!
                </h3>
                <div className="mt-2 text-sm text-green-700">
                  <p><strong>{lastRegisteredUser.name}</strong> ({lastRegisteredUser.email}) has been added to the system with the role of <strong>{lastRegisteredUser.role}</strong>.</p>
                  <p className="mt-1">The new user will appear in the list below.</p>
                </div>
              </div>
              <div className="ml-3 flex-shrink-0">
                <button
                  onClick={() => setRegistrationSuccess(false)}
                  className="inline-flex rounded-md bg-green-50 p-1.5 text-green-500 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 focus:ring-offset-green-50"
                >
                  <span className="sr-only">Dismiss</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Tab Bar ─────────────────────────────────────────────────────
            The seventh module sub-nav, and it was missed when the other six were fixed because it
            lives in a route file rather than in a `NavBar.jsx`. Same shape, same result: five tabs
            needing ~619px inside 358px pushed `documentElement.scrollWidth` to 1085 on a 390px
            phone, and `body { overflow-x: hidden }` meant the page would not pan to reach them.
            Measured: `⚙️ Settings` 0px of 86 visible, `🔏 Signing rules` 10px of 118.
            `overflow-x-auto` is the same answer used in the six module NavBars. */}
        <div className="mb-6 border-b border-gray-200 overflow-x-auto">
          <nav className="-mb-px flex space-x-6">
            <button
              onClick={() => setActiveTab('users')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 whitespace-nowrap shrink-0 transition-colors ${
                activeTab === 'users'
                  ? 'border-red-600 text-red-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              👤 Users
            </button>
            {(user?.role === 'master_admin' || user?.role === 'admin') && (
              <button
                onClick={() => setActiveTab('roles')}
                className={`pb-3 px-1 text-sm font-medium border-b-2 whitespace-nowrap shrink-0 transition-colors ${
                  activeTab === 'roles'
                    ? 'border-red-600 text-red-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                🔑 Roles
              </button>
            )}
            {canAssignForms && (
              <button
                onClick={() => setActiveTab('forms')}
                className={`pb-3 px-1 text-sm font-medium border-b-2 whitespace-nowrap shrink-0 transition-colors ${
                  activeTab === 'forms'
                    ? 'border-red-600 text-red-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                📝 Form assignments
              </button>
            )}
            {canAssignForms && (
              <button
                onClick={() => setActiveTab('signing')}
                className={`pb-3 px-1 text-sm font-medium border-b-2 whitespace-nowrap shrink-0 transition-colors ${
                  activeTab === 'signing'
                    ? 'border-red-600 text-red-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                🔏 Signing rules
              </button>
            )}
            <button
              onClick={() => setActiveTab('settings')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 whitespace-nowrap shrink-0 transition-colors ${
                activeTab === 'settings'
                  ? 'border-red-600 text-red-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              ⚙️ Settings
            </button>
          </nav>
        </div>

        {/* Register New User */}
        {activeTab === 'users' && (
        <Card
          title="User Administration"
          headerActions={
            <Button
              variant="primary"
              onClick={() => setShowRegisterForm(!showRegisterForm)}
              className="bfs-button-primary"
            >
              {showRegisterForm ? 'Cancel' : 'Register New User'}
            </Button>
          }
          className="mb-8"
        >
          {showRegisterForm && (
            <form onSubmit={handleRegisterSubmit} className="space-y-6 mb-8 p-6 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900">Register New User</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Input
                  label="First Name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  error={formErrors.first_name}
                  required
                />

                <Input
                  label="Last Name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  error={formErrors.last_name}
                  required
                />
              </div>

              <Input
                label="Email Address"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                error={formErrors.email}
                required
              />

              <Input
                label="Password"
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                error={formErrors.password}
                placeholder="Minimum 8 characters"
                required
              />

              <PhoneInput
                label="Phone Number"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                error={formErrors.phone}
                placeholder="(555) 123-4567"
              />

              <div>
                <label className="bfs-label">Role</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className="bfs-input"
                  required
                >
                  {roles.length > 0 ? (
                    roles
                      .filter(r => user?.role === 'master_admin' || !['admin', 'master_admin'].includes(r.name))
                      .map(r => (
                        <option key={r.name} value={r.name}>{r.display_name}</option>
                      ))
                  ) : (
                    <>
                      <option value="employee">Employee</option>
                      <option value="manager">Manager</option>
                      <option value="client">Client</option>
                      {user?.role === 'master_admin' && <option value="admin">Admin</option>}
                      {user?.role === 'master_admin' && <option value="master_admin">Master Admin</option>}
                    </>
                  )}
                </select>
              </div>

              {/* WHAT LANGUAGE THIS PERSON READS. Here, at onboarding, because that is the only
                  moment somebody who does not read English can be handed a screen they can read —
                  expecting them to find a settings page written in a language they cannot read is the
                  same chicken-and-egg the sign-in page has. Blank is a real answer: their phone and
                  the company setting decide, which is right for most people. */}
              <div>
                <label className="bfs-label" htmlFor="new-user-language">Language</label>
                <select
                  id="new-user-language"
                  name="language"
                  value={formData.language}
                  onChange={handleInputChange}
                  className="bfs-input"
                >
                  <option value="">Their device decides</option>
                  {Object.keys(LANGUAGE_NAMES).map(tag => (
                    /* The language's own name — never a code and never a flag. A flag is a country,
                       and a Mexican hand and a Salvadoran hand share no flag but share this language. */
                    <option key={tag} value={tag}>{languageName(tag)}</option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowRegisterForm(false)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={submitting}
                  disabled={submitting}
                >
                  {submitting ? 'Registering...' : 'Register User'}
                </Button>
              </div>
            </form>
          )}

          {/* Users List */}
          {loading ? (
            <LoadingSpinner text="Loading users..." />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Language
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Module Access
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Permissions
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((userItem) => {
                    const userName = userItem.first_name && userItem.last_name 
                      ? `${userItem.first_name} ${userItem.last_name}` 
                      : userItem.name || userItem.email;
                      
                    const isCurrentUser = userItem.id === user?.id;
                    
                    return (
                      <tr key={userItem.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {userName}
                              {isCurrentUser && (
                                <span className="ml-2 text-xs text-blue-600">(You)</span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">
                              {userItem.email}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col space-y-2">
                            {(() => {
                              const roleObj = roles.find(r => r.name === userItem.role)
                              const colorClass = roleObj
                                ? (ROLE_COLOR_CLASSES[roleObj.color] || ROLE_COLOR_CLASSES.gray)
                                : (userItem.role === 'master_admin' ? 'bg-fuchsia-100 text-fuchsia-800 border border-fuchsia-300'
                                  : userItem.role === 'admin' ? 'bg-red-100 text-red-800'
                                  : 'bg-gray-100 text-gray-800')
                              return (
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
                                  {roleObj ? roleObj.display_name : userItem.role}
                                </span>
                              )
                            })()}
                            
                            {/* Role management dropdown */}
                            {!isCurrentUser && (
                              <div className="flex flex-col space-y-1">
                                {/* Only admins can change roles */}
                                {((user?.role === 'admin' && userItem.role !== 'admin' && userItem.role !== 'master_admin') || 
                                   (user?.role === 'master_admin')) && (
                                  <div className="flex items-center space-x-2">
                                    <select
                                      value={roleChanges[userItem.id] || userItem.role}
                                      onChange={(e) => handleRoleChange(userItem.id, e.target.value)}
                                      className="text-xs py-1 pl-2 pr-6 border border-gray-300 rounded"
                                      disabled={confirmingRoleChange === userItem.id}
                                    >
                                      {roles.length > 0 ? (
                                        roles
                                          .filter(r => user?.role === 'master_admin' || !['admin', 'master_admin'].includes(r.name))
                                          .map(r => (
                                            <option key={r.name} value={r.name}>{r.display_name}</option>
                                          ))
                                      ) : (
                                        <>
                                          <option value="employee">Employee</option>
                                          <option value="manager">Manager</option>
                                          <option value="client">Client</option>
                                          {user?.role === 'master_admin' && (
                                            <>
                                              <option value="admin">Admin</option>
                                              <option value="master_admin">Master Admin</option>
                                            </>
                                          )}
                                        </>
                                      )}
                                    </select>
                                    
                                    {/* Confirm button appears when a new role is selected */}
                                    {confirmingRoleChange === userItem.id && roleChanges[userItem.id] && roleChanges[userItem.id] !== userItem.role && (
                                      <div className="flex items-center space-x-1">
                                        <button
                                          className="text-xs bg-green-100 hover:bg-green-200 text-green-800 py-1 px-2 rounded"
                                          onClick={() => handleRoleChangeConfirm(userItem.id, userName, userItem.role, roleChanges[userItem.id])}
                                        >
                                          Confirm
                                        </button>
                                        <button
                                          className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-800 py-1 px-2 rounded"
                                          onClick={() => {
                                            setConfirmingRoleChange(null);
                                            setRoleChanges(prev => ({ ...prev, [userItem.id]: null }));
                                          }}
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {isCurrentUser ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Active
                            </span>
                          ) : (
                            <button
                              onClick={() => handleToggleActive(userItem)}
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${
                                userItem.is_active 
                                  ? 'bg-green-100 text-green-800 hover:bg-red-100 hover:text-red-800' 
                                  : 'bg-gray-100 text-gray-800 hover:bg-green-100 hover:text-green-800'
                              }`}
                              title={userItem.is_active ? 'Click to deactivate' : 'Click to reactivate'}
                            >
                              {userItem.is_active ? 'Active' : 'Inactive'}
                            </button>
                          )}
                        </td>
                        {/* SET IT FOR SOMEBODY WHO ALREADY HAS AN ACCOUNT. The register form covers a
                            new hire; this covers the whole existing workforce, which is where the
                            owner's problem actually is today — everyone who needs this most already
                            has a login and cannot read the screen where they would change it. */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <select
                            aria-label={`Language for ${userName}`}
                            value={selectedLanguage(userItem.language)}
                            onChange={(e) => handleUserLanguageChange(userItem.id, e.target.value)}
                            className="text-xs py-1 pl-2 pr-6 border border-gray-300 rounded"
                          >
                            {/* Blank is a real answer, not an absence of one: their device and the
                                company setting decide. Never defaulted to English — that would look
                                like a choice, and a choice outranks both.

                                `selectedLanguage`, not the raw column: the server accepts a regional
                                tag and a raw `es-MX` matches no option here, so the row would report
                                "Their device" for somebody whose account is set to Spanish. */}
                            <option value="">Their device</option>
                            {!LANGUAGE_NAMES[selectedLanguage(userItem.language)]
                              && selectedLanguage(userItem.language) && (
                              <option value={selectedLanguage(userItem.language)}>
                                {languageName(userItem.language)}
                              </option>
                            )}
                            {Object.keys(LANGUAGE_NAMES).map(tag => (
                              <option key={tag} value={tag}>{languageName(tag)}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {userItem.created_at ? new Date(userItem.created_at).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {userItem.role === 'master_admin' ? (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                              All Modules
                            </span>
                          ) : userItem.accessible_modules && userItem.accessible_modules.length > 0 ? (
                            <div className="flex items-center space-x-2">
                              <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">
                                Custom ({userItem.accessible_modules.length})
                              </span>
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => handleEditModules(userItem)}
                              >
                                Edit
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded">
                                Role Defaults
                              </span>
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => handleEditModules(userItem)}
                              >
                                Customize
                              </Button>
                            </div>
                          )}
                        </td>
                        {/* Tier 2 & 3 permissions column */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {userItem.role === 'master_admin' || userItem.role === 'admin' ? (
                            <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                              Full Access
                            </span>
                          ) : (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => handleOpenAccessModal(userItem)}
                            >
                              Edit Access
                            </Button>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            {!isCurrentUser && user?.role === 'master_admin' && (
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => handleOpenResetPassword(userItem)}
                              >
                                🔑 Reset PW
                              </Button>
                            )}
                            {!isCurrentUser && 
                             !(user?.role === 'admin' && userItem.role === 'master_admin') &&
                             !(user?.role === 'admin' && userItem.role === 'admin') && (
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleDeleteUser(userItem.id, userName)}
                                disabled={confirmingRoleChange === userItem.id}
                              >
                                Delete
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              
              {users.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500">No users found</p>
                </div>
              )}
            </div>
          )}
        </Card>
        )}

        {/* ── Role Management ─────────────────────────────────────────────── */}
        {activeTab === 'roles' && (user?.role === 'master_admin' || user?.role === 'admin') && (
          <Card
            title="Role Management"
            headerActions={
              <Button
                variant="primary"
                onClick={() => {
                  setEditingRole(null)
                  setRoleForm({ name: '', display_name: '', description: '', color: 'gray', default_modules: [], default_permissions: [] })
                  setRoleModalOpen(true)
                }}
              >
                + New Role
              </Button>
            }
            className="mb-8"
          >
            {rolesLoading ? (
              <LoadingSpinner text="Loading roles..." />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-2">
                {roles.map(role => {
                  const badgeClass = ROLE_COLOR_CLASSES[role.color] || ROLE_COLOR_CLASSES.gray
                  return (
                    <div key={role.id} className="border border-gray-200 rounded-lg p-4 bg-white hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${badgeClass}`}>
                          {role.display_name}
                        </span>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${role.is_system ? 'bg-gray-100 text-gray-500' : 'bg-blue-50 text-blue-600'}`}>
                          {role.is_system ? 'System' : 'Custom'}
                        </span>
                      </div>
                      {role.description && (
                        <p className="text-xs text-gray-500 mt-1 mb-2 line-clamp-2">{role.description}</p>
                      )}
                      <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                        <span>{role.default_modules.length} modules</span>
                        <span>·</span>
                        <span>{role.default_permissions.length} permissions</span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                          onClick={() => {
                            setEditingRole(role)
                            setRoleForm({
                              name: role.name,
                              display_name: role.display_name,
                              description: role.description || '',
                              color: role.color || 'gray',
                              default_modules: [...role.default_modules],
                              default_permissions: [...role.default_permissions],
                            })
                            setRoleModalOpen(true)
                          }}
                        >
                          Edit
                        </button>
                        {!role.is_system && (
                          <button
                            className="text-xs text-red-500 hover:text-red-700 font-medium"
                            disabled={deletingRoleId === role.id}
                            onClick={async () => {
                              if (!window.confirm(`Delete the "${role.display_name}" role? This cannot be undone.`)) return
                              setDeletingRoleId(role.id)
                              try {
                                await identityService.deleteRole(role.id)
                                toast.showSuccess(`Role "${role.display_name}" deleted`)
                                loadRoles()
                              } catch (err) {
                                toast.showError(err?.response?.data?.detail || 'Failed to delete role')
                              } finally {
                                setDeletingRoleId(null)
                              }
                            }}
                          >
                            {deletingRoleId === role.id ? 'Deleting...' : 'Delete'}
                          </button>
                        )}
                      </div>
                    </div>
                  )
                })}
                {roles.length === 0 && (
                  <div className="col-span-full text-center py-8 text-gray-500">
                    No roles found. Roles are seeded on server startup.
                  </div>
                )}
              </div>
            )}
          </Card>
        )}

        {/* ── Form assignments ───────────────────────────────────────────── */}
        {activeTab === 'forms' && canAssignForms && (
          <Suspense fallback={<div className="py-12 flex justify-center"><LoadingSpinner /></div>}>
            <AssignmentManager />
          </Suspense>
        )}

        {/* ── Signing rules (the QR door's identity policy) ───────────────── */}
        {activeTab === 'signing' && canAssignForms && (
          <Suspense fallback={<div className="py-12 flex justify-center"><LoadingSpinner /></div>}>
            <div className="max-w-4xl space-y-3">
              <p className="text-sm text-gray-500">
                What the platform asks for — and what it actually checks — when somebody with no login signs a
                safety record from a QR sticker. This applies to every sticker in the company at once. The
                stickers themselves are issued under <strong>Fleet → Stickers</strong>, because deciding which
                machines carry an inspection sheet is equipment work.
              </p>
              <IdentityStrictness canAssign={canAssignForms} />
            </div>
          </Suspense>
        )}

        {/* ── Brand Settings ─────────────────────────────────────────────── */}
        {activeTab === 'settings' && (
        <div className="space-y-8 mb-8">
          {/* THE COMPANY'S LANGUAGE. First in this tab, and above the brand editor, because it is the
              only setting on this page that changes a screen somebody sees BEFORE they can sign in. */}
          <Card>
            <CompanyLanguageSetting canEdit={isAdmin()} />
          </Card>

          {/* Field ticket emails. Sits directly under the language setting because it is the other
              setting on this page that reaches somebody OUTSIDE the company — a customer's inbox —
              and so deserves to be read before the brand editor rather than after it. */}
          <Card>
            <FieldTicketEmailSettings />
          </Card>

          {/* Directly under the field-ticket switches deliberately: both answer "what happens when
              we email a customer", and the person turning on automatic copies is exactly the person
              who should be asked where the replies to them will land. */}
          <Card>
            <ReplyToSettings />
          </Card>

          {/* The company's own consent to the AI Office Worker. Shown only where the module is released
              to this company: a card about an add-on the company cannot use would only raise questions. */}
          {isModuleReleased('office_worker', moduleSwitches) && (
            <Card>
              <AiOfficeWorkerSettings />
            </Card>
          )}

          {/* How long people stay signed in. Below the outward-facing email settings and above the
              brand editor: it is the one setting on this page that every employee feels every day,
              and the one whose default was, until now, a number nobody had chosen. */}
          <Card>
            <SessionPolicySettings />
          </Card>
          {user?.role === 'master_admin' && <TenantBrandEditor mode="tenant" />}
          <BrandSettings />
          {/* Bid Workup's schedule presets go with Bid Workup (review OFF-5). */}
          {isModuleReleased('bidworkup', moduleSwitches) && <ScheduleSettings />}
        </div>
        )}

      </div>

      {/* Module Access Modal */}
      {editingModules && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-modal p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-900">
                Edit Module Access
              </h2>
              <button
                onClick={handleCancelEditModules}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>User:</strong> {editingModules.name} ({editingModules.email})
                </p>
                <p className="text-sm text-gray-700 mt-1">
                  <strong>Role:</strong> {editingModules.role}
                </p>
              </div>

              <div className="mb-4 p-4 bg-blue-50 border-l-4 border-blue-400 rounded">
                <p className="text-sm text-blue-800 font-medium mb-2">
                  {selectedModules.length > 0 
                    ? 'Custom Module Access (overrides role defaults)' 
                    : 'Using Role Default Access'}
                </p>
                <p className="text-sm text-blue-700">
                  {selectedModules.length > 0 
                    ? 'Select modules below to customize access. Click "Reset to Role Defaults" to clear custom assignments.'
                    : `This ${editingModules.role} role has default access to: ${visibleRoleDefaultNames(editingModules.role) || 'No modules'}`}
                </p>
              </div>

              <div className="space-y-3">
                {visibleModules.map(module => {
                  const isRoleDefault = getRoleDefaultModules(editingModules.role).includes(module.id)
                  const isCustomSelected = selectedModules.includes(module.id)
                  const isActive = selectedModules.length > 0 ? isCustomSelected : isRoleDefault
                  
                  return (
                    <label
                      key={module.id}
                      className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all ${
                        isActive 
                          ? 'border-blue-400 bg-blue-50' 
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isActive}
                        onChange={() => handleModuleToggle(module.id)}
                        className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <span className="ml-3 text-2xl">{module.icon}</span>
                      <span className="ml-3 flex-1 text-sm font-medium text-gray-900">
                        {module.name}
                      </span>
                      {selectedModules.length === 0 && isRoleDefault && (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">
                          Role Default
                        </span>
                      )}
                      {isCustomSelected && (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                          Custom
                        </span>
                      )}
                    </label>
                  )
                })}
              </div>
            </div>

            <div className="flex justify-between items-center p-6 border-t bg-gray-50">
              <Button
                variant="outline"
                onClick={handleResetToDefault}
                disabled={savingModules}
                className="text-orange-600 border-orange-300 hover:bg-orange-50"
              >
                🔄 Reset to Role Defaults
              </Button>
              <div className="flex space-x-3">
                <Button
                  variant="secondary"
                  onClick={handleCancelEditModules}
                  disabled={savingModules}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSaveModules}
                  loading={savingModules}
                  disabled={savingModules}
                >
                  {savingModules ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Permissions & Record Access Modal ─────────────────────────── */}
      {accessModalUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-modal p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">

            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b flex-shrink-0">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Permissions &amp; Access</h2>
                <p className="text-sm text-gray-500 mt-0.5">{accessModalUser.name} ({accessModalUser.email})</p>
              </div>
              <button onClick={handleCloseAccessModal} className="text-gray-400 hover:text-gray-600">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b flex-shrink-0">
              <button
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  accessModalTab === 'permissions'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setAccessModalTab('permissions')}
              >
                Feature Permissions
              </button>
              <button
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  accessModalTab === 'record'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setAccessModalTab('record')}
              >
                Record Access
              </button>
            </div>

            {/* Body */}
            <div className="p-6 overflow-y-auto flex-1">
              {accessModalLoading ? (
                <div className="flex justify-center py-8"><LoadingSpinner text="Loading..." /></div>
              ) : accessModalTab === 'permissions' ? (
                /* ── Tier 2: Feature Permission Checkboxes ── */
                <div className="space-y-5">
                  <p className="text-sm text-gray-600 bg-blue-50 border-l-4 border-blue-400 p-3 rounded">
                    These flags fine-tune what the user can do within modules, independent of their
                    base role. ADMIN and MASTER_ADMIN always have full access and ignore these flags.
                  </p>
                  {/* Group permissions */}
                  {Array.from(new Set(visiblePermissions.map(p => p.group))).map(group => (
                    <div key={group}>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        {group}
                      </h4>
                      <div className="space-y-1.5">
                        {visiblePermissions.filter(p => p.group === group).map(perm => (
                          <label key={perm.key} title={perm.description} className="flex items-center gap-3 cursor-pointer group">
                            <input
                              type="checkbox"
                              checked={editingPermissions.includes(perm.key)}
                              onChange={() => handlePermissionToggle(perm.key)}
                              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700 group-hover:text-gray-900">
                              {perm.label}
                            </span>
                            <code className="text-xs text-gray-400 ml-auto">{perm.key}</code>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* ── Tier 3: Record-level Access Modes & Grants ── */
                <div className="space-y-6">
                  <p className="text-sm text-gray-600 bg-amber-50 border-l-4 border-amber-400 p-3 rounded">
                    <strong>Open</strong> (default) — user sees all records in that module.&nbsp;
                    <strong>Restricted</strong> — user sees only the specific records you grant below.
                    ADMIN and MASTER_ADMIN always see everything regardless of this setting.
                  </p>

                  {RECORD_ACCESS_MODULES.map(mod => {
                    const mode = editingAccessModes[mod.id] || 'open'
                    const grants = editingRecordGrants[mod.id] || []
                    return (
                      <div key={mod.id} className="border rounded-lg overflow-hidden">
                        {/* Module header with mode toggle */}
                        <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
                          <span className="font-medium text-gray-800">{mod.label}</span>
                          <div className="flex items-center gap-1 bg-white border rounded-md p-0.5">
                            <button
                              className={`px-3 py-1 text-xs rounded transition-colors ${
                                mode === 'open'
                                  ? 'bg-green-100 text-green-800 font-semibold'
                                  : 'text-gray-500 hover:bg-gray-100'
                              }`}
                              onClick={() => handleAccessModeToggle(mod.id, 'open')}
                            >
                              Open
                            </button>
                            <button
                              className={`px-3 py-1 text-xs rounded transition-colors ${
                                mode === 'restricted'
                                  ? 'bg-amber-100 text-amber-800 font-semibold'
                                  : 'text-gray-500 hover:bg-gray-100'
                              }`}
                              onClick={() => handleAccessModeToggle(mod.id, 'restricted')}
                            >
                              Restricted
                            </button>
                          </div>
                        </div>

                        {/* Record grant picker (only when restricted) */}
                        {mode === 'restricted' && (
                          <div className="p-4">
                            {mod.id === 'projects' && recordGrantProjects.length > 0 ? (
                              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                                {recordGrantProjects.map(proj => (
                                  <label key={proj.id} className="flex items-center gap-3 cursor-pointer group">
                                    <input
                                      type="checkbox"
                                      checked={grants.includes(proj.id)}
                                      onChange={() => handleRecordGrantToggle(mod.id, proj.id)}
                                      className="w-4 h-4 rounded border-gray-300 text-blue-600"
                                    />
                                    <span className="text-sm text-gray-700">
                                      {proj.project_name}
                                      <span className="ml-2 text-xs text-gray-400">{proj.project_number}</span>
                                    </span>
                                  </label>
                                ))}
                              </div>
                            ) : mod.id === 'crm' && recordGrantClients.length > 0 ? (
                              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                                {recordGrantClients.map(client => (
                                  <label key={client.id} className="flex items-center gap-3 cursor-pointer group">
                                    <input
                                      type="checkbox"
                                      checked={grants.includes(client.id)}
                                      onChange={() => handleRecordGrantToggle(mod.id, client.id)}
                                      className="w-4 h-4 rounded border-gray-300 text-blue-600"
                                    />
                                    <span className="text-sm text-gray-700">
                                      {client.company_name}
                                      <span className="ml-2 text-xs text-gray-400">{client.client_number}</span>
                                    </span>
                                  </label>
                                ))}
                              </div>
                            ) : mod.id === 'documents' ? (
                              <div className="space-y-1.5">
                                <p className="text-xs text-gray-500 mb-2">
                                  Select which document types this user can see.{' '}
                                  <span className="text-blue-600">Project restrictions also apply</span> — if the user
                                  is restricted on Projects, they will only see documents tied to their granted projects.
                                </p>
                                <div className="grid grid-cols-2 gap-1.5">
                                  {DOCUMENT_TYPES.map(dt => (
                                    <label key={dt.value} className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={grants.includes(dt.value)}
                                        onChange={() => handleRecordGrantToggle(mod.id, dt.value)}
                                        className="w-4 h-4 rounded border-gray-300 text-blue-600"
                                      />
                                      <span className="text-sm text-gray-700">{dt.label}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <div className="text-sm text-gray-500 italic">
                                {mod.id === 'projects'
                                  ? 'No projects available (Projects service may be offline)'
                                  : mod.id === 'crm'
                                  ? 'No clients available (CRM service may be offline)'
                                  : `No records available for ${mod.label}.`}
                                {grants.length > 0 && (
                                  <p className="mt-1 text-xs text-gray-400">
                                    {grants.length} record{grants.length !== 1 ? 's' : ''} currently granted.
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 p-6 border-t flex-shrink-0">
              <Button variant="secondary" onClick={handleCloseAccessModal} disabled={savingAccess}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSaveAccess} loading={savingAccess} disabled={savingAccess}>
                {savingAccess ? 'Saving...' : 'Save Access Settings'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Role Create / Edit Modal ─────────────────────────────────────── */}
      {roleModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-modal p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingRole ? `Edit Role: ${editingRole.display_name}` : 'Create New Role'}
              </h2>
              <button
                onClick={() => setRoleModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-light leading-none"
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto flex-1 p-6 space-y-5">
              {/* Name slug — only for new roles */}
              {!editingRole && (
                <div>
                  <label className="bfs-label">Role Name (slug) <span className="text-gray-400 font-normal">— lowercase letters, digits, underscores only</span></label>
                  <input
                    className="bfs-input"
                    value={roleForm.name}
                    onChange={e => setRoleForm(f => ({ ...f, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') }))}
                    placeholder="e.g. field_supervisor"
                  />
                </div>
              )}

              {/* Display Name */}
              <div>
                <label className="bfs-label">Display Name</label>
                <input
                  className="bfs-input"
                  value={roleForm.display_name}
                  onChange={e => setRoleForm(f => ({ ...f, display_name: e.target.value }))}
                  placeholder="e.g. Field Supervisor"
                />
              </div>

              {/* Description */}
              <div>
                <label className="bfs-label">Description <span className="text-gray-400 font-normal">(optional)</span></label>
                <textarea
                  className="bfs-input"
                  rows={2}
                  value={roleForm.description}
                  onChange={e => setRoleForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Brief description of this role's purpose"
                />
              </div>

              {/* Color */}
              <div>
                <label className="bfs-label">Badge Color</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {ROLE_COLORS.map(color => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setRoleForm(f => ({ ...f, color }))}
                      className={`px-3 py-1 rounded-full text-xs font-medium border-2 transition-all ${ROLE_COLOR_CLASSES[color]} ${roleForm.color === color ? 'border-gray-700 scale-110' : 'border-transparent'}`}
                    >
                      {color}
                    </button>
                  ))}
                </div>
              </div>

              {/* Default Modules */}
              <div>
                <label className="bfs-label">Default Module Access</label>
                <p className="text-xs text-gray-500 mb-2">These modules will be enabled by default when a user is assigned this role. Admins can still customize per-user.</p>
                <div className="grid grid-cols-2 gap-2 border border-gray-200 rounded p-3 bg-gray-50">
                  {visibleModules.map(mod => (
                    <label key={mod.id} className="flex items-center gap-2 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-gray-300 text-blue-600"
                        checked={roleForm.default_modules.includes(mod.id)}
                        onChange={() => handleRoleFormModuleToggle(mod.id)}
                      />
                      <span className={mod.comingSoon ? 'text-gray-400' : 'text-gray-700'}>
                        {mod.icon} {mod.name}{mod.comingSoon ? ' (soon)' : ''}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Default Permissions */}
              <div>
                <label className="bfs-label">Default Feature Permissions</label>
                <p className="text-xs text-gray-500 mb-2">These Tier 2 permissions will be granted by default. Admins can still customize per-user.</p>
                <div className="space-y-3 border border-gray-200 rounded p-3 bg-gray-50">
                  {Object.entries(
                    visiblePermissions.reduce((acc, p) => {
                      if (!acc[p.group]) acc[p.group] = []
                      acc[p.group].push(p)
                      return acc
                    }, {})
                  ).map(([group, perms]) => (
                    <div key={group}>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{group}</p>
                      <div className="grid grid-cols-2 gap-1">
                        {perms.map(p => (
                          <label key={p.key} title={p.description} className="flex items-center gap-2 cursor-pointer text-sm">
                            <input
                              type="checkbox"
                              className="w-4 h-4 rounded border-gray-300 text-blue-600"
                              checked={roleForm.default_permissions.includes(p.key)}
                              onChange={() => handleRoleFormPermToggle(p.key)}
                            />
                            <span className="text-gray-700">{p.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 p-6 border-t flex-shrink-0">
              <Button variant="secondary" onClick={() => setRoleModalOpen(false)} disabled={savingRole}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSaveRole} loading={savingRole} disabled={savingRole}>
                {savingRole ? 'Saving...' : editingRole ? 'Save Changes' : 'Create Role'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Admin Password Reset Modal ─────────────────────────────────────── */}
      {resetPasswordUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-modal p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Reset User Password</h2>
                <p className="text-sm text-gray-500 mt-0.5">{resetPasswordUser.name} ({resetPasswordUser.email})</p>
              </div>
              <button onClick={handleCloseResetPassword} className="text-gray-400 hover:text-gray-600">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4">
              <div className="p-3 bg-amber-50 border-l-4 border-amber-400 rounded text-sm text-amber-800">
                <strong>⚠️ Warning:</strong> This will immediately change this user's password. They will need to use the new password on their next login.
              </div>

              <div>
                <label className="bfs-label">New Password</label>
                <input
                  type="password"
                  className="bfs-input"
                  value={resetPasswordValue}
                  onChange={(e) => {
                    setResetPasswordValue(e.target.value)
                    setResetPasswordError('')
                  }}
                  placeholder="Minimum 8 characters"
                  autoFocus
                />
              </div>

              <div>
                <label className="bfs-label">Confirm New Password</label>
                <input
                  type="password"
                  className="bfs-input"
                  value={resetPasswordConfirm}
                  onChange={(e) => {
                    setResetPasswordConfirm(e.target.value)
                    setResetPasswordError('')
                  }}
                  placeholder="Re-enter password"
                />
              </div>

              {resetPasswordError && (
                <div className="p-3 bg-red-50 border-l-4 border-red-400 rounded text-sm text-red-700">
                  {resetPasswordError}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 p-6 border-t">
              <Button variant="secondary" onClick={handleCloseResetPassword} disabled={resettingPassword}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleResetPasswordSubmit} loading={resettingPassword} disabled={resettingPassword}>
                {resettingPassword ? 'Setting Password...' : 'Set New Password'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Admin
